import moment from "moment"

export const DEFAULT_SPECIAL_EVENT_TIME_ZONE = "UTC"

export const SPECIAL_EVENT_TIME_ZONE_OPTIONS = [
  { label: "UTC", value: "UTC" },
  { label: "UTC-08:00 / UTC-07:00 - US Pacific (Los Angeles)", value: "America/Los_Angeles" },
  { label: "UTC-07:00 / UTC-06:00 - US Mountain (Denver)", value: "America/Denver" },
  { label: "UTC-06:00 / UTC-05:00 - US Central (Chicago)", value: "America/Chicago" },
  { label: "UTC-05:00 / UTC-04:00 - US Eastern (New York)", value: "America/New_York" },
  { label: "UTC+00:00 / UTC+01:00 - United Kingdom (London)", value: "Europe/London" },
  { label: "UTC+01:00 / UTC+02:00 - Central Europe (Berlin)", value: "Europe/Berlin" },
  { label: "UTC+02:00 / UTC+03:00 - Israel (Jerusalem)", value: "Asia/Jerusalem" },
  { label: "UTC+05:30 - India (Kolkata)", value: "Asia/Kolkata" },
  { label: "UTC+09:00 - Japan (Tokyo)", value: "Asia/Tokyo" },
  { label: "UTC+10:00 / UTC+11:00 - Australia Eastern (Sydney)", value: "Australia/Sydney" },
  { label: "UTC-12:00 fixed offset", value: "UTC-12:00" },
  { label: "UTC-11:00 fixed offset", value: "UTC-11:00" },
  { label: "UTC-10:00 fixed offset", value: "UTC-10:00" },
  { label: "UTC-09:00 fixed offset", value: "UTC-09:00" },
  { label: "UTC-08:00 fixed offset", value: "UTC-08:00" },
  { label: "UTC-07:00 fixed offset", value: "UTC-07:00" },
  { label: "UTC-06:00 fixed offset", value: "UTC-06:00" },
  { label: "UTC-05:00 fixed offset", value: "UTC-05:00" },
  { label: "UTC-04:00 fixed offset", value: "UTC-04:00" },
  { label: "UTC-03:00 fixed offset", value: "UTC-03:00" },
  { label: "UTC-02:00 fixed offset", value: "UTC-02:00" },
  { label: "UTC-01:00 fixed offset", value: "UTC-01:00" },
  { label: "UTC+01:00 fixed offset", value: "UTC+01:00" },
  { label: "UTC+02:00 fixed offset", value: "UTC+02:00" },
  { label: "UTC+03:00 fixed offset", value: "UTC+03:00" },
  { label: "UTC+04:00 fixed offset", value: "UTC+04:00" },
  { label: "UTC+05:00 fixed offset", value: "UTC+05:00" },
  { label: "UTC+06:00 fixed offset", value: "UTC+06:00" },
  { label: "UTC+07:00 fixed offset", value: "UTC+07:00" },
  { label: "UTC+08:00 fixed offset", value: "UTC+08:00" },
  { label: "UTC+09:00 fixed offset", value: "UTC+09:00" },
  { label: "UTC+10:00 fixed offset", value: "UTC+10:00" },
  { label: "UTC+11:00 fixed offset", value: "UTC+11:00" },
  { label: "UTC+12:00 fixed offset", value: "UTC+12:00" },
]

type DateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const getPartsInTimeZone = (date: Date, timeZone: string): DateParts => {
  const fixedOffsetMs = getFixedOffsetMs(timeZone)
  if (fixedOffsetMs !== undefined) {
    const shiftedDate = new Date(date.getTime() + fixedOffsetMs)

    return {
      year: shiftedDate.getUTCFullYear(),
      month: shiftedDate.getUTCMonth() + 1,
      day: shiftedDate.getUTCDate(),
      hour: shiftedDate.getUTCHours(),
      minute: shiftedDate.getUTCMinutes(),
      second: shiftedDate.getUTCSeconds(),
    }
  }

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date)

  const values = parts.reduce<Record<string, number>>((acc, part) => {
    if (part.type !== "literal") {
      acc[part.type] = Number(part.value)
    }

    return acc
  }, {})

  return {
    year: values.year,
    month: values.month,
    day: values.day,
    hour: values.hour,
    minute: values.minute,
    second: values.second,
  }
}

const getFixedOffsetMs = (timeZone: string) => {
  const match = /^UTC([+-])(\d{2}):(\d{2})$/.exec(timeZone)
  if (!match) return undefined

  const sign = match[1] === "+" ? 1 : -1
  const hours = Number(match[2])
  const minutes = Number(match[3])

  return sign * ((hours * 60) + minutes) * 60 * 1000
}

const getTimeZoneOffsetMs = (date: Date, timeZone: string) => {
  const fixedOffsetMs = getFixedOffsetMs(timeZone)
  if (fixedOffsetMs !== undefined) return fixedOffsetMs

  const parts = getPartsInTimeZone(date, timeZone)
  const zonedTimeAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    date.getUTCMilliseconds(),
  )

  return zonedTimeAsUtc - date.getTime()
}

export const convertUtcToZonedWallTime = (
  value: moment.MomentInput,
  timeZone = DEFAULT_SPECIAL_EVENT_TIME_ZONE,
): moment.Moment => {
  const utcDate = moment(value).toDate()
  const parts = getPartsInTimeZone(utcDate, timeZone)

  return moment({
    year: parts.year,
    month: parts.month - 1,
    day: parts.day,
    hour: parts.hour,
    minute: parts.minute,
    second: parts.second,
    millisecond: moment(value).millisecond(),
  })
}

export const convertZonedWallTimeToUtc = (
  value: moment.Moment,
  timeZone = DEFAULT_SPECIAL_EVENT_TIME_ZONE,
): moment.Moment => {
  const wallTimeAsUtc = Date.UTC(
    value.year(),
    value.month(),
    value.date(),
    value.hour(),
    value.minute(),
    value.second(),
    value.millisecond(),
  )
  const firstOffset = getTimeZoneOffsetMs(new Date(wallTimeAsUtc), timeZone)
  const firstUtc = new Date(wallTimeAsUtc - firstOffset)
  const refinedOffset = getTimeZoneOffsetMs(firstUtc, timeZone)

  return moment(new Date(wallTimeAsUtc - refinedOffset))
}

export const convertDateRangeBetweenTimeZones = (
  dates: moment.Moment[] | undefined,
  fromTimeZone: string,
  toTimeZone: string,
): moment.Moment[] | undefined => {
  if (!dates?.[0] || !dates?.[1]) return dates

  return dates.map(date => convertUtcToZonedWallTime(
    convertZonedWallTimeToUtc(date, fromTimeZone),
    toTimeZone,
  ))
}
